/**
 * API Endpoints
 *
 * Centralized definition of all API endpoints used in the application.
 * This file serves as a single source of truth for API paths.
 */

export const API_ENDPOINTS = {
  // ============================================
  // Authentication
  // ============================================
  AUTH: {
    /** POST - Login with username, password, and reCAPTCHA token */
    LOGIN: '/api/auth/login',
    /** POST - Refresh the access token using refresh token */
    REFRESH: '/api/auth/refresh',
    /** POST - Logout and invalidate tokens */
    LOGOUT: '/api/auth/logout',
    /** GET - Get current authenticated user details */
    ME: '/api/payroll-platform/org-user/me',
  },

  // ============================================
  // Organizations
  // ============================================
  ORGANIZATION: {
    /** POST - Get paginated list of organizations */
    LIST: '/api/payroll-platform/organization/get-org-list',
    /** GET - Get organization by ID */
    GET_BY_ID: (id: string) => `/api/payroll-platform/organization/get-org-by-id/${id}`,
    /** POST - Get users by organization ID */
    USERS: '/api/payroll-platform/organization/get-user-by-orgId',
    /** POST - Get pending organization registrations */
    PENDING_LIST: '/api/payroll-platform/organization/get-pending-organization-registration-list',
    /** POST - Verify OTP for support session */
    VERIFY_OTP: '/api/payroll-platform/organization/verify-support-session-otp',
    /** POST - Create a new organization */
    CREATE: '/api/payroll-platform/org-registration/create',
  },

  // ============================================
  // Subscription Plans
  // ============================================
  SUBSCRIPTION: {
    /** GET - Get organization's current subscription plan */
    GET_ORG_PLAN: (id: string) => `/api/payroll-platform/org-plan/get-org-plan/${id}`,
    /** GET - Get all available subscription plans */
    PLAN_LIST: '/api/payroll-platform/org-plan/get-subscription-plan-list',
    /** POST - Get paginated subscription plan grid list (with terms) */
    PLAN_GRID_LIST: '/api/payroll-platform/org-plan/subscription-plan-grid-list',
    /** POST - Create a new subscription plan */
    CREATE_SUBSCRIPTION_PLAN: '/api/payroll-platform/org-plan/create-subscription-plan',
    /** POST - Update an existing subscription plan */
    UPDATE_SUBSCRIPTION_PLAN: '/api/payroll-platform/org-plan/update-subscription-plan',
    /** POST - Add a term to a subscription plan */
    ADD_SUBSCRIPTION_PLAN_TERM: '/api/payroll-platform/org-plan/add-subscription-plan-term',
    /** POST - Update a subscription plan term */
    UPDATE_SUBSCRIPTION_PLAN_TERM: '/api/payroll-platform/org-plan/update-subscription-plan-term',
    /** GET - Get subscription plans (new endpoint) */
    PLAN_LIST_NEW: '/api/payroll-platform/org-plan/get-subscription-plan-list-new',
    /** POST - Upgrade subscription plan */
    UPGRADE: '/api/payroll-platform/org-plan/upgrade-subscription-plan',
    /** GET - Get plan terms by subscription plan ID */
    GET_PLAN_TERMS_BY_PLAN_ID: (planId: string) =>
      `/api/payroll-platform/org-plan/get-planTerms-by-planId/${planId}`,
  },

  // ============================================
  // Platform Users
  // ============================================
  USER: {
    /** POST - Get paginated list of platform users */
    LIST: '/api/payroll-platform/org-user/get-user-list',
    /** POST - Create a new platform user */
    CREATE: '/api/payroll-platform/org-user/create',
    /** POST - Update an existing user */
    UPDATE: '/api/payroll-platform/org-user/update',
    /** GET - Get user by ID */
    GET_BY_ID: (id: string) => `/api/payroll-platform/org-user/get-by-id/${id}`,
  },

  // ============================================
  // Login Audit
  // ============================================
  AUDIT: {
    /** POST - Get login users organization grid */
    LOGIN_GRID: '/api/login-user-audit/get-loginUsers-org-grid',
    /** POST - Get detailed login user audit history */
    HISTORY: '/api/login-user-audit/get-loginUserAudit-history',
    /** POST - Get audit logs by auditId (generic, used across all pages) */
    LOGS_LIST: '/api/payroll-platform/audit/audit-logs-list',
  },
  // ============================================
  // Organization Registration OTP
  // ============================================
  ORG_REGISTRATION: {
    /** POST - Send SMS OTP for mobile verification */
    SEND_SMS_OTP: '/api/payroll-platform/org-registration/send-sms-otp',
    /** POST - Verify SMS OTP */
    VERIFY_SMS_OTP: '/api/payroll-platform/org-registration/verify-sms-otp',
    /** POST - Send email OTP for email verification */
    SEND_EMAIL_OTP: '/api/payroll-platform/org-registration/send-email-otp',
    /** POST - Verify email OTP */
    VERIFY_EMAIL: '/api/payroll-platform/org-registration/verify-email-otp',
    /** POST - Assign allowed plans to a platform user */
    CREATE_ALLOWED_PLANS: '/api/payroll-platform/org-registration/create/allowed-plans-to-platform-user',
    /** GET - Get allowed plans for a user by userId */
    ALLOWED_PLANS_BY_USER: (userId: string) =>
      `/api/payroll-platform/org-registration/allowed-plan/list-by-userId/${userId}`,
    /** GET - Get allowed plan details (with terms) for a user by userId */
    ALLOWED_PLAN_DETAILS_BY_USER: (userId: string) =>
      `/api/payroll-platform/org-registration/allowed-plan/details-by-userId/${userId}`,
  },

  // ============================================
  // RBAC — Roles & Permissions (dynamic, backend-driven)
  // ============================================
  RBAC: {
    /** GET - Caller's own effective permission set (bootstraps the UI store) */
    MY_PERMISSIONS: '/api/rbac/my-permissions',
    /** GET - Full permission catalog (populates the role editor) */
    PERMISSIONS: '/api/rbac/permissions',
    /** GET - List all roles (roles grid) */
    ROLES: '/api/rbac/roles',
    /** GET - Full detail of one role */
    ROLE_BY_ID: (roleId: string) => `/api/rbac/roles/${roleId}`,
    /** POST - Create a role (code, name, description, parentRoleId) */
    ROLE_CREATE: '/api/rbac/roles/create',
    /** POST - Update a role (name/description/parent/status, version) */
    ROLE_UPDATE: '/api/rbac/roles/update',
    /** POST - Delete a role (reassignToRoleId if it has assignees) */
    ROLE_DELETE: '/api/rbac/roles/delete',
    /** POST - Low-level grant/deny replace: { roleId, permissions: [{permKey, effect}] } */
    ROLE_PERMISSIONS: '/api/rbac/roles/permissions',
    /** POST - Reset a SYSTEM role's permissions to seed defaults: { roleId } */
    ROLE_RESET: '/api/rbac/roles/reset',
    /** GET - Grouped, role-independent permission tree (module → section → permission) */
    PERMISSION_TREE: '/api/rbac/ui/permission-tree',
    /** GET (load) / POST (save) - Toggle matrix for a role — the Roles & Permissions page */
    ROLE_MATRIX: (roleId: string) => `/api/rbac/ui/roles/${roleId}/matrix`,
    /** POST - Assign a role to a user (optional validFrom/validUntil) */
    ASSIGN: '/api/rbac/assignments/assign',
    /** POST - Revoke a role from a user */
    REVOKE: '/api/rbac/assignments/revoke',
    /** GET - List a user's role assignments */
    USER_ROLES: (userId: string) => `/api/rbac/users/${userId}/roles`,
  },
} as const;

// Type helper for endpoint functions
export type EndpointFunction = (id: string) => string;
